<script setup>
import { computed, ref, watch } from "vue"
import debounce from "lodash.debounce"
import { useStore } from "vuex"
import { apiUrl, defaultImage } from "@common/helpers"
import { ListLoader as Loading } from  "@projetsolaire/ui"
import { SearchInput} from "@projetsolaire/ui"

const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value()
      }
    }
    document.addEventListener("click", el.clickOutsideEvent)
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent)
  },
}

const store = useStore()
const query = ref("")
const showProductList = ref(false)
const loading = ref(false)
const items = ref([])
const isFocused = ref(false)
watch(query, (newValue) => {
  if (newValue.length > 0) {
    showProductList.value = true
  }
})

const debounceFn = debounce(async (value) => {
  try {
    loading.value = true
    const result = await store.dispatch("products/fetchAll", {
      categories: ["HARDWARE"],
      title: value ? value : null,
    })
    items.value = result.items
  } catch (e) {
    console.log("e: ", e)
  } finally {
    loading.value = false
  }
}, 500)

const onHandleFocusOut = () => {
  showProductList.value = false
}
const onHandleFocus = () => {
  isFocused.value = true
  if (query.value.length > 0) {
    showProductList.value = true
  }
}
</script>

<template>
  <div
    id="searchbar-desktop"
    v-click-outside="() => onHandleFocusOut()"
    class="relative"
  >
    <div>
      <div>
        <SearchInput
          v-model="query"
          :placeholder="$t('common.shop.hardware.navigation.search-bar.placeholder')"
          @input="debounceFn"
          @focus="onHandleFocus"
          @blur="isFocused = false"
        />
        <div
          v-if="query.length > 0 && showProductList"
          class="absolute inset-x-6 top-full z-50 max-h-[65vh] overflow-auto rounded-b-xl border bg-white shadow-lg"
        >
          <Loading v-if="loading" />
          <div
            v-else-if="items.length > 0"
            class="p-2 md:p-4 col-span-full relative"
          >
            <h4 class="mb-2 px-2 font-bold text-neutral">
              {{ $t("common.shop.hardware.search-bar.results") }}
            </h4>
            <ul>
              <li
                v-for="(item, index) in items"
                :key="index"
                class="bullet-none block"
              >
                <div :index-name="`products_${index}`">
                  <router-link
                    class="flex rounded px-4 py-2 text-base font-semibold no-underline hover:bg-gray-200 hover:no-underline"
                    :to="{
                      name: 'HardwareMarketplaceShopProduct',
                      params: { slug: item.slug },
                    }"
                    @click="
                      () => {
                        query = ''
                        showProductList = false
                      }
                    "
                    ><img
                      class="mr-4 aspect-square w-16 h-16 object-contain object-center"
                      :src="
                        item.thumbnailImage
                          ? apiUrl(item.thumbnailImage.thumbnail.url)
                          : defaultImage
                      "
                      :alt="item.title"
                    />
                    <div>
                      <div>
                        <span>{{ item.title }}</span>
                      </div>
                      <div
                        v-if="item.sku"
                        class="text-sm font-thin text-gray-500"
                      >
                        <span>{{ item.sku }}</span>
                      </div>
                    </div></router-link
                  >
                </div>
              </li>
            </ul>
          </div>
          <div
            class="flex w-full px-4 py-2"
            :class="items.length > 0 ? 'bg-gray-200' : 'bg-white'"
          >
            <svg
              data-v-60641edd=""
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              class="mr-2 h-4 w-4"
              width="20"
            >
              <path
                d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z"
              ></path>
              <path
                d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z"
              ></path>
            </svg>
            {{ $t("common.shop.hardware.search-bar.search-for") }}
            <strong class="ml-1 group-hover:underline pr-1"
              >{{ query }}
            </strong>
            <span v-if="items.length === 0">
              - {{ $t("common.shop.hardware.search-bar.no-results") }}
            </span>
          </div>
        </div>
        <!---->
      </div>
    </div>
  </div>
</template>
